/**
 * Presentation canvas — slide-deck view for `kind: "presentation"` artifacts.
 *
 * Layout: thumbnail sidebar on the left, single-slide stage on the right.
 * The artifact body is parsed into top-level `<section>` elements; each
 * slide renders inside its own sandboxed iframe (CSS-scaled in the
 * thumbnails, full-size on the stage) so the model's Tailwind classes work
 * without leaking styles into the host app.
 *
 * Streaming behavior matches the html canvas: creates and updates buffer
 * through `pendingContent` so the renderer never shows a half-constructed
 * deck. A skeleton occupies the canvas during initial generation.
 *
 * @module components/artifacts/presentation-canvas
 */

import type { PendingSlides } from "@taqat/core/types";
import { motion } from "framer-motion";
import { Download, Loader2, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { downloadArtifactPdf } from "@/lib/artifacts/download";
import { parseSlides } from "@/lib/artifacts/slides";
import { useArtifactStore } from "@/store/artifact-store";
import { api } from "@/utils/api-client";
import { ArtifactErrorBanner } from "./artifact-error-banner";
import { FocusModeToggle } from "./focus-mode-toggle";
import { GenerationSkeleton } from "./generation-skeleton";
import { SlideDeckView, type DisplaySlide } from "./slide-deck-view";

interface FetchedArtifact {
  id: string;
  title: string;
  content: string;
  updatedAt?: string;
}

/**
 * Builds the slide list for an in-flight edit: untouched slides come from the
 * still-visible `content`; the slide(s) being changed keep their *current*
 * markup under a loader and swap to the finished version once, on finish.
 *
 * We deliberately do NOT render the partially-streamed html: re-rendering the
 * slide's iframe on every delta makes it flicker. So the edited slide stays
 * visually stable (old content + spinner) during the edit, then the finished
 * deck swaps in atomically. `pendingSlides.byId` still tracks the stream, but
 * only to know which slides are involved — not to paint intermediate frames.
 */
function buildEditingSlides(currentContent: string, ps: PendingSlides): DisplaySlide[] {
  const originals = new Map(parseSlides(currentContent).map((s) => [s.id, s.html]));
  const editing = new Set(ps.editing);
  const out: DisplaySlide[] = [];
  for (const id of ps.order) {
    // A slide is part of this edit if it was announced or has started streaming.
    const involved = editing.has(id) || Boolean(ps.byId[id]);
    if (involved) {
      // Stable current slide (or a placeholder for a brand-new slide) + loader.
      out.push({ id, html: originals.get(id) || placeholderSection(id), streaming: true });
    } else {
      const html = originals.get(id);
      if (html) out.push({ id, html, streaming: false });
    }
  }
  return out;
}

/** Minimal placeholder shown for a brand-new slide before its first chunk. */
function placeholderSection(id: string): string {
  return `<section data-slide-id="${id}" class="p-12 min-h-[540px] flex items-center justify-center"><p class="text-lg text-slate-400">Generating…</p></section>`;
}

/**
 * Builds the slide list for a fresh create: complete slides parsed from
 * `content` (the ground truth, streamed as raw deltas — unchanged since v1),
 * plus one placeholder card for the slide currently generating.
 *
 * Unlike `buildEditingSlides`, there is no "old" content to protect from
 * flicker here — the in-progress slide is brand new — but we still don't
 * paint its partial, likely-unbalanced HTML into an iframe live; a stable
 * placeholder card is enough to make the sidebar grow the moment generation
 * of the next slide starts, rather than only once `content` shows it closed.
 */
export function buildCreatingSlides(content: string, ps: PendingSlides): DisplaySlide[] {
  const complete = parseSlides(content).map((s) => ({ ...s, streaming: false }));
  const completeIds = new Set(complete.map((s) => s.id));
  const inFlightId = Object.keys(ps.byId).find((id) => !completeIds.has(id));
  if (!inFlightId) return complete;
  return [...complete, { id: inFlightId, html: placeholderSection(inFlightId), streaming: true }];
}

/**
 * Side-by-side presentation canvas. Mounts via the kind dispatcher in
 * `artifact.tsx` whenever the active artifact is a presentation.
 */
export function PresentationCanvas() {
  const artifact = useArtifactStore((s) => s.artifact);
  const setArtifact = useArtifactStore((s) => s.setArtifact);
  const hideArtifact = useArtifactStore((s) => s.hideArtifact);

  const [fetchedAt, setFetchedAt] = useState<string | undefined>(undefined);
  /** PDF render+download takes several seconds — swap the toolbar icon for a spinner. */
  const [isDownloading, setIsDownloading] = useState(false);

  const isStreaming = artifact.status === "streaming";
  const isUpdateInFlight = artifact.pendingContent !== null;
  const isStreamingCreate = isStreaming && !isUpdateInFlight;
  const pendingSlides = artifact.pendingSlides;

  /**
   * Slides to render, each tagged with whether it is mid-edit.
   *
   * - **Create**: parsed complete `<section>`s from `content` (the raw delta
   *   stream, unchanged since v1) plus one trailing placeholder card for the
   *   slide currently being generated — `pendingSlides` reports it the moment
   *   its opening tag streams in, well before `content` would show it closed,
   *   so the sidebar grows the instant the next slide starts instead of only
   *   once it finishes (see `buildCreatingSlides`).
   * - **Idle**: parse the single body, nothing in flight.
   * - **Update**: render per-slide from `pendingSlides` — untouched slides come
   *   from the still-visible `content`, the changed slide(s) stream in. The
   *   deck stays on screen and only the edited slide shows a loader, instead of
   *   blanking the whole canvas. Before the plan arrives (or if a model skips
   *   it) we keep the current deck visible.
   */
  const displaySlides = useMemo<DisplaySlide[]>(() => {
    if (isUpdateInFlight && pendingSlides) {
      return buildEditingSlides(artifact.content, pendingSlides);
    }
    if (isStreamingCreate && pendingSlides) {
      return buildCreatingSlides(artifact.content, pendingSlides);
    }
    return parseSlides(artifact.content).map((s) => ({ ...s, streaming: false }));
  }, [isUpdateInFlight, isStreamingCreate, pendingSlides, artifact.content]);

  // Skeleton only until the first slide (complete or in-progress) appears;
  // updates always keep the current deck visible.
  const hasNoSlidesYet = isStreamingCreate && displaySlides.length === 0;
  const [activeId, setActiveId] = useState<string>("");

  const userSelectedRef = useRef(false);

  /**
   * Create: follow the latest slide as the deck builds so the user watches it
   * fill in. Disabled once the user manually selects a thumbnail.
   */
  useEffect(() => {
    if (!isStreamingCreate) return;
    if (userSelectedRef.current) return;
    if (displaySlides.length === 0) return;
    const latest = displaySlides[displaySlides.length - 1];
    if (latest && latest.id !== activeId) setActiveId(latest.id);
  }, [isStreamingCreate, displaySlides, activeId]);

  /**
   * Edit: jump the stage to the slide being edited the moment the plan lands,
   * overriding any earlier manual selection so the user always sees the change
   * happen. Falls back to the first slide that started streaming when the model
   * didn't announce an editing set.
   */
  const editingTargetId =
    isUpdateInFlight && pendingSlides
      ? (pendingSlides.editing[0] ?? Object.keys(pendingSlides.byId)[0] ?? null)
      : null;
  useEffect(() => {
    if (!editingTargetId) return;
    userSelectedRef.current = false;
    setActiveId(editingTargetId);
  }, [editingTargetId]);

  /** Reset the user-selected flag whenever a new artifact opens. */
  useEffect(() => {
    userSelectedRef.current = false;
  }, [artifact.id]);

  function handleSelectSlide(id: string) {
    userSelectedRef.current = true;
    setActiveId(id);
  }

  /**
   * Track which slide is in focus. When the deck content changes (open,
   * stream finish, edit) snap to the first slide if the active id no
   * longer exists in the new deck.
   */
  useEffect(() => {
    if (displaySlides.length === 0) {
      if (activeId !== "") setActiveId("");
      return;
    }
    if (!displaySlides.some((s) => s.id === activeId)) {
      setActiveId(displaySlides[0].id);
    }
  }, [displaySlides, activeId]);

  const activeSlide =
    displaySlides.find((s) => s.id === activeId) ?? displaySlides[0] ?? null;

  /**
   * Fetch the canonical artifact body when the canvas opens or the id
   * changes. Mirrors the html canvas — streamed deltas reach the store
   * first, but a fresh open after a page reload needs the persisted body.
   */
  useEffect(() => {
    if (!artifact.isVisible || !artifact.id || artifact.status === "streaming") return;
    let cancelled = false;
    api
      .get(`/artifacts/${artifact.id}`)
      .then((response) => {
        if (cancelled) return;
        const fetched = (response as { data?: FetchedArtifact })?.data;
        if (!fetched) return;
        setFetchedAt(fetched.updatedAt);
        if (fetched.content && fetched.content !== artifact.content) {
          setArtifact({ content: fetched.content, title: fetched.title });
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [artifact.isVisible, artifact.id, artifact.status, artifact.content, setArtifact]);

  async function handleDownload() {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      await downloadArtifactPdf({ id: artifact.id, title: artifact.title });
    } finally {
      setIsDownloading(false);
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, x: 80, scale: 0.96 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 80, scale: 0.96 }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      className="relative flex h-full min-w-0 flex-[3] flex-col border-l bg-background shadow-2xl max-lg:fixed max-lg:inset-0 max-lg:z-40 max-lg:flex-none max-lg:border-l-0"
    >
      <header className="flex shrink-0 items-center gap-3 border-b bg-background px-4 py-2.5">
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-base font-semibold leading-tight">
            {artifact.title || "Untitled"}
          </h2>
          <SubtitleRow
            fetchedAt={fetchedAt}
            isStreaming={isStreaming}
            isUpdateInFlight={isUpdateInFlight}
            slideCount={displaySlides.length}
            activeIndex={activeSlide
              ? displaySlides.findIndex((s) => s.id === activeSlide.id)
              : -1}
          />
        </div>

        <FocusModeToggle />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={handleDownload}
          disabled={isDownloading}
          title={isDownloading ? "Preparing PDF…" : "Download PDF"}
        >
          {isDownloading ? (
            <Loader2 className="size-5 animate-spin" />
          ) : (
            <Download className="size-5" />
          )}
        </Button>
        <Button type="button" variant="ghost" size="icon" onClick={hideArtifact} title="Close">
          <X className="size-5" />
        </Button>
      </header>

      <div className="flex flex-1 overflow-hidden bg-muted/20">
        {hasNoSlidesYet ? (
          <GenerationSkeleton kind="presentation" title={artifact.title} />
        ) : (
          <SlideDeckView
            slides={displaySlides}
            title={artifact.title}
            activeId={activeSlide?.id ?? ""}
            onSelectSlide={handleSelectSlide}
            isStreamingCreate={isStreamingCreate}
          />
        )}
      </div>

      {/*
        * Updates stream into the same skeleton+sidebar path as creates
        * (see `sourceHtml` above), so we don't mount the full-screen
        * `ArtifactUpdateOverlay`. The error/retry banner stays.
        */}
      <ArtifactErrorBanner />
    </motion.div>
  );
}

/** Subtitle row — same shape as the other canvases plus a slide counter. */
function SubtitleRow({
  fetchedAt,
  isStreaming,
  isUpdateInFlight,
  slideCount,
  activeIndex,
}: {
  fetchedAt: string | undefined;
  isStreaming: boolean;
  isUpdateInFlight: boolean;
  slideCount: number;
  activeIndex: number;
}) {
  const timeLabel = fetchedAt ? updatedLabel(fetchedAt) : null;
  const status =
    isUpdateInFlight ? "Updating…" : isStreaming ? "Generating…" : null;
  const slideLabel =
    slideCount > 0 && activeIndex >= 0
      ? `Slide ${activeIndex + 1} of ${slideCount}`
      : null;

  if (!timeLabel && !status && !slideLabel)
    return <span className="text-xs text-muted-foreground">&nbsp;</span>;

  return (
    <span className="text-xs text-muted-foreground">
      {slideLabel}
      {slideLabel && (timeLabel || status) ? (
        <span className="mx-1.5 opacity-50">|</span>
      ) : null}
      {timeLabel}
      {timeLabel && status ? <span className="mx-1.5 opacity-50">|</span> : null}
      {status}
    </span>
  );
}

function updatedLabel(timestamp?: string): string {
  if (!timestamp) return "Recently updated";
  const updated = new Date(timestamp);
  if (Number.isNaN(updated.getTime())) return "Recently updated";
  const diffMs = Date.now() - updated.getTime();
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} day${days === 1 ? "" : "s"} ago`;
  return updated.toLocaleDateString();
}
